import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useChainNext,
  useRunResults,
  useSession,
  useStartRun,
} from '../../../../lib/workflows.hooks.js';
import type { AlisonRunCopy, WorkflowDetail, WorkflowRun } from '../../workflows.types.js';
import {
  type AlisonSetup,
  configFields,
  type LiveProgress,
  liveSession,
  pickAll,
  stepParams,
} from './alisonLive.js';
import {
  type AlisonActionDisplay,
  type AlisonSession,
  type ChainCard,
  fillTemplate,
  reroute,
} from './alisonRun.js';

export interface AlisonLiveRun {
  session: AlisonSession | null;
  start: (path: string[], stopAt: number, setup: AlisonSetup) => void;
  // Runs the path again from `index` with `changes` on top of its params, dropping every step
  // after it. A step the run has not reached yet only takes the changes. Only the changes are
  // kept, so the rest still follows the step before.
  restartFrom: (index: number, changes: Record<string, unknown>) => void;
  stop: () => void;
  runOf: (actionType: string | null | undefined) => WorkflowRun | null;
  // The path being run and where it stops: a step's result can re-route Alison's way on.
  plan: { path: string[]; stopAt: number } | null;
  // The params a step ran with, or will run with when it has not started: what it inherits
  // from the step before, with the user's changes on top.
  paramsOf: (actionType: string) => Record<string, unknown> | null;
}

// Drives the chain from the browser through the same startRun/chainNext a user would call:
// closing the tab stops it. The server-side runner replaces this.
export function useAlisonLiveRun(
  cards: ChainCard[],
  details: WorkflowDetail[],
  displays: Record<string, AlisonActionDisplay>,
  // Null only before the catalog arrives, when no run can have started.
  copy: AlisonRunCopy | null,
): AlisonLiveRun {
  const [progress, setProgress] = useState<LiveProgress | null>(null);
  // Bumped on every start and stop, so a response for an abandoned run is dropped.
  const token = useRef(0);
  const submitted = useRef(0);
  const startRun = useStartRun();
  const chainNext = useChainNext();
  const { runs: results, error: resultsError } = useRunResults(progress?.runIds ?? []);
  const { data: session = null, error: sessionError } = useSession(
    progress?.runIds[0] ?? null,
    (data) =>
      progress !== null && liveSession(progress, data ?? null, () => null).status === 'running',
    true,
  );
  const pollError = sessionError?.message ?? resultsError;

  const fail = useCallback((current: number, message: string): void => {
    if (token.current !== current) return;
    setProgress((state) => (state === null ? null : { ...state, failed: state.failed ?? message }));
  }, []);
  const fieldsOf = (actionType: string | undefined) =>
    configFields(details.find((detail) => detail.workflowId === actionType));

  useEffect(() => {
    if (pollError !== null) fail(token.current, pollError);
  }, [pollError, fail]);

  const mutateNext = chainNext.mutate;
  useEffect(() => {
    if (progress === null || progress.failed !== null) return;
    const index = progress.runIds.length - 1;
    const source = results[index];
    if (index < 0 || index >= progress.stopAt || source === undefined || source === null) return;
    if (source.status !== 'completed' || submitted.current > index) return;
    const next = progress.path[index + 1];
    const detail = details.find((entry) => entry.workflowId === next);
    if (next === undefined || detail === undefined) return;
    const current = token.current;
    const edge = source.nextActions.find((action) => action.actionType === next);
    if (edge === undefined || !edge.enabled) {
      // The result ruled out Alison's planned step (a Banner brief, say): she goes the way the
      // service recommends off it instead, and this effect submits that next render.
      const path = reroute(cards, progress.path, index, source.nextActions);
      if (path !== null) {
        const end = path.length - 1;
        const stopAt =
          progress.stopAt === progress.path.length - 1 ? end : Math.min(progress.stopAt, end);
        setProgress({ ...progress, path, stopAt });
        return;
      }
      submitted.current = index + 1;
      fail(current, edge?.reason ?? `${next} is not offered`);
      return;
    }
    submitted.current = index + 1;
    const pick = pickAll(
      source,
      edge,
      configFields(detail),
      progress.params[index] ?? {},
      progress.setup.leadingKpi,
    );
    pick.params = { ...pick.params, ...progress.setup.paramsByAction?.[next] };
    if (edge.requiresSelection && pick.count === 0) {
      const from = progress.path[index] ?? '';
      fail(
        current,
        fillTemplate(copy?.nothingToPick ?? '', { source: displays[from]?.title ?? from }),
      );
      return;
    }
    mutateNext(
      {
        runId: source.runId,
        actionType: next,
        selectItems: pick.selectItems,
        paramsOverride: pick.params,
      },
      {
        onSuccess: ({ runId }) => {
          if (token.current !== current) return;
          setProgress((state) =>
            state === null
              ? null
              : {
                  ...state,
                  runIds: [...state.runIds, runId],
                  params: [...state.params, pick.params],
                  picked: [...state.picked, pick.count],
                },
          );
        },
        onError: (error) => fail(current, error.message),
      },
    );
  }, [progress, results, cards, details, displays, copy, mutateNext, fail]);

  const calls = (index: number): string | null => {
    const count = progress?.picked[index];
    const source = progress?.path[index - 1];
    if (index === 0 || count === undefined || source === undefined || copy === null) return null;
    return fillTemplate(copy.allPicked, {
      count,
      source: displays[source]?.title ?? source,
    });
  };

  const paramsAt = (index: number): Record<string, unknown> | null => {
    const actionType = progress?.path[index];
    if (progress === null || actionType === undefined) return null;
    const ran = progress.params[index];
    if (ran !== undefined) return ran;
    return {
      ...stepParams(fieldsOf(actionType), paramsAt(index - 1) ?? {}, progress.setup.leadingKpi),
      ...progress.setup.paramsByAction?.[actionType],
    };
  };

  const start: AlisonLiveRun['start'] = (path, stopAt, setup) => {
    const root = path[0];
    if (root === undefined) return;
    token.current += 1;
    submitted.current = 0;
    const current = token.current;
    const params = setup.params ?? stepParams(fieldsOf(root), {}, setup.leadingKpi);
    setProgress({
      path,
      stopAt,
      setup,
      runIds: [],
      params: [params],
      picked: [0],
      failed: null,
    });
    const callbacks = {
      onSuccess: ({ runId }: { runId: string }) => {
        if (token.current !== current) return;
        setProgress((state) => (state === null ? null : { ...state, runIds: [runId] }));
      },
      onError: (error: { message: string }) => fail(current, error.message),
    };
    if (setup.parent !== undefined) {
      chainNext.mutate({ ...setup.parent, actionType: root, paramsOverride: params }, callbacks);
    } else {
      startRun.mutate({ actionType: root, scope: setup.scope, params }, callbacks);
    }
  };

  return {
    session: progress === null ? null : liveSession(progress, session, calls),
    plan: progress === null ? null : { path: progress.path, stopAt: progress.stopAt },
    start,
    restartFrom: (index, changes) => {
      const actionType = progress?.path[index];
      if (progress === null || actionType === undefined) return;
      if (index === 0) {
        const params = { ...progress.params[0], ...changes };
        start(progress.path, progress.stopAt, { ...progress.setup, params });
        return;
      }
      const setup = {
        ...progress.setup,
        paramsByAction: {
          ...progress.setup.paramsByAction,
          [actionType]: { ...progress.setup.paramsByAction?.[actionType], ...changes },
        },
      };
      // Not reached yet: it picks the new params up when it gets there.
      if (index > progress.runIds.length) {
        setProgress({ ...progress, setup });
        return;
      }
      // Also a step that failed to start or is still starting: its old request is dropped.
      token.current += 1;
      // The step before `index` is submitted again, now with the new params.
      submitted.current = index - 1;
      setProgress({
        ...progress,
        setup,
        runIds: progress.runIds.slice(0, index),
        params: progress.params.slice(0, index),
        picked: progress.picked.slice(0, index),
        failed: null,
      });
    },
    stop: () => {
      token.current += 1;
      setProgress(null);
    },
    runOf: (actionType) => {
      const index = actionType == null ? -1 : (progress?.path.indexOf(actionType) ?? -1);
      return index < 0 ? null : (results[index] ?? null);
    },
    paramsOf: (actionType) => {
      const index = progress?.path.indexOf(actionType) ?? -1;
      return index < 0 ? null : paramsAt(index);
    },
  };
}
